import { useEffect, useState } from 'react';

import { useSettingsStore, type ThemeMode } from '@/features/settings/store';

/**
 * JS mirror of the CSS custom properties in `index.css`, for the few places
 * that need a value rather than a class — chart fills, category swatches.
 * Keep the two in step.
 */
export const palette = {
  light: {
    bg: '#f6f7fb',
    surface: '#ffffff',
    elevated: '#f1f2f8',
    fg: '#11132a',
    muted: '#676c87',
    line: '#e5e7f0',
    primary: '#4f46e5',
    success: '#059669',
    danger: '#e11d48',
    warning: '#d97706',
  },
  dark: {
    bg: '#0a0c18',
    surface: '#121527',
    elevated: '#1a1e36',
    fg: '#eef0fb',
    muted: '#959bba',
    line: '#242a45',
    primary: '#818cf8',
    success: '#34d399',
    danger: '#fb7185',
    warning: '#fbbf24',
  },
} as const;

/**
 * Categorical series colours for charts, validated with the dataviz palette
 * checker against this app's own surfaces (light `#ffffff`, dark `#0f172a`):
 * lightness band, chroma floor, CVD separation, normal-vision floor and 3:1
 * contrast all pass in both modes. Blue↔orange is the pair that survives
 * protanopia and deuteranopia, which is why it is not the app's indigo/violet. The
 * dark surface has since moved to `#121527`; re-check before relying on it.
 *
 * Re-run `validate_palette.js` before changing either value.
 */
export const SERIES_COLORS = {
  light: { primary: '#2a78d6', secondary: '#eb6834' },
  dark: { primary: '#3987e5', secondary: '#d95926' },
} as const;

function resolve(mode: ThemeMode): boolean {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/**
 * Applies the theme preference to `<html class="dark">` and, while the mode is
 * "system", keeps following the OS if the user flips it with the tab open.
 */
export function useThemeSync(): void {
  const themeMode = useSettingsStore((state) => state.themeMode);

  useEffect(() => {
    const apply = () =>
      document.documentElement.classList.toggle('dark', resolve(themeMode));

    apply();
    if (themeMode !== 'system') return;

    const query = window.matchMedia('(prefers-color-scheme: dark)');
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, [themeMode]);
}

/** Whether the dark palette is currently in effect — for chart colours. */
export function useIsDark(): boolean {
  const themeMode = useSettingsStore((state) => state.themeMode);
  const [systemDark, setSystemDark] = useState(
    () => window.matchMedia('(prefers-color-scheme: dark)').matches
  );

  useEffect(() => {
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setSystemDark(query.matches);
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  return themeMode === 'dark' || (themeMode === 'system' && systemDark);
}

/** The validated series pair for the active theme. */
export function useSeriesColors() {
  return useIsDark() ? SERIES_COLORS.dark : SERIES_COLORS.light;
}
